import { useDebounce } from '@uidotdev/usehooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Control, useForm, useWatch } from 'react-hook-form';
import {
  PropsWithChildren,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import type { ApiError } from '../../../../../../@types';
import { MarketType } from '../../../../../../@types/api';
import { QUERY_KEYS } from '../../../../../../constants';
import API_ROUTES from '../../../../../../constants/apiRoutes';
import { useToast } from '../../../../../../contexts/ToastContext';
import useInfiniteScroll from '../../../../../../hooks/useInfiniteScroll';
import { useToastErrorMessage } from '../../../../../../hooks/useToastErrorMessage';
import { useMarketExclusionSports } from '../../../../../../hooks/useMarketExclusionSports';
import { ensureArray } from '../../../../../../utils';
import { getData, postData } from '../../../../../../utils/api';
import {
  BonusMarketTypeExclusionsResponse,
  MARKET_TYPE_EXCLUSIONS_DEFAULT_VALUES,
  MarketTypeExclusionsFilterFormData,
  getMarketTypesFromExclusions,
  getMarketTypesToAdd,
} from '../marketExclusions.utils';

type PickerState = {
  selectedMarketTypes: MarketType[];
  checkedMarketTypeIds: string[];
  search: string;
};

type PickerAction =
  | { type: 'hydrate'; marketTypes: MarketType[] }
  | { type: 'toggle'; marketTypeId: string }
  | { type: 'setChecked'; marketTypeIds: string[]; checked: boolean }
  | { type: 'add'; marketTypes: MarketType[] }
  | { type: 'remove'; marketTypeId: string }
  | { type: 'search'; search: string }
  | { type: 'clearChecked' }
  | { type: 'clearAll' };

const initialState: PickerState = {
  selectedMarketTypes: [],
  checkedMarketTypeIds: [],
  search: '',
};

const pickerReducer = (state: PickerState, action: PickerAction): PickerState => {
  switch (action.type) {
    case 'hydrate':
      return { ...state, selectedMarketTypes: action.marketTypes };

    case 'toggle':
      return {
        ...state,
        checkedMarketTypeIds: state.checkedMarketTypeIds.includes(action.marketTypeId)
          ? state.checkedMarketTypeIds.filter((id) => id !== action.marketTypeId)
          : [...state.checkedMarketTypeIds, action.marketTypeId],
      };

    case 'setChecked':
      return {
        ...state,
        checkedMarketTypeIds: action.checked
          ? Array.from(new Set([...state.checkedMarketTypeIds, ...action.marketTypeIds]))
          : state.checkedMarketTypeIds.filter((id) => !action.marketTypeIds.includes(id)),
      };

    case 'add': {
      const selectedById = new Map(state.selectedMarketTypes.map((marketType) => [marketType.id, marketType]));

      action.marketTypes.forEach((marketType) => selectedById.set(marketType.id, marketType));

      return { ...state, selectedMarketTypes: Array.from(selectedById.values()), checkedMarketTypeIds: [] };
    }

    case 'remove':
      return {
        ...state,
        selectedMarketTypes: state.selectedMarketTypes.filter(({ id }) => id !== action.marketTypeId),
      };

    case 'search':
      return { ...state, search: action.search };

    case 'clearChecked':
      return { ...state, checkedMarketTypeIds: [] };

    case 'clearAll':
      return { ...state, selectedMarketTypes: [] };

    default:
      return state;
  }
};

type PickerContextValue = {
  state: {
    availableMarketTypes: MarketType[];
    selectedMarketTypes: MarketType[];
    checkedMarketTypeIds: Set<string>;
    selectedMarketTypeIds: Set<string>;
    search: string;
    allLoadedChecked: boolean;
    someLoadedChecked: boolean;
    hasEligibleMarketTypes: boolean;
  };
  actions: {
    setSearch: (search: string) => void;
    toggleMarketType: (marketTypeId: string) => void;
    setAllLoadedChecked: (checked: boolean) => void;
    addSelected: () => void;
    removeMarketType: (marketTypeId: string) => void;
    clearAll: () => void;
    save: () => void;
  };
  meta: {
    control: Control<MarketTypeExclusionsFilterFormData>;
    sportOptions: Array<{ id: string; name: string }>;
    listRef: RefObject<HTMLUListElement | null>;
    lastElementRef: (node: HTMLElement | null) => void;
    isLoading: boolean;
    isFetchingNextPage: boolean;
    disableSave: boolean;
    closeModal?: () => void;
  };
};

const PickerContext = createContext<PickerContextValue | null>(null);

type ProviderProps = PropsWithChildren<{
  closeModal?: () => void;
}>;

export const MarketPickerProvider = ({ children, closeModal }: ProviderProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();
  const { sportOptions } = useMarketExclusionSports();

  const [state, dispatch] = useReducer(pickerReducer, initialState);
  const hasInitializedDraft = useRef(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Candidate filters are independent of the accumulated exclusion draft.
  const { control, reset } = useForm<MarketTypeExclusionsFilterFormData>({
    defaultValues: MARKET_TYPE_EXCLUSIONS_DEFAULT_VALUES,
  });

  const [sportId, usedFor, type] = useWatch({ control, name: ['sportId', 'usedFor', 'type'] });
  const debouncedSearch = useDebounce(state.search.trim(), 300);

  // Saved exclusions and available candidates have separate query lifecycles.
  const exclusionsQuery = useQuery<BonusMarketTypeExclusionsResponse, ApiError>({
    queryKey: [QUERY_KEYS.bonusMarketTypeExclusions],
    queryFn: () => getData<BonusMarketTypeExclusionsResponse>(API_ROUTES.BONUS_MARKET_TYPE_EXCLUSIONS),
  });

  useEffect(() => {
    if (exclusionsQuery.error) handleErrorMessage(exclusionsQuery.error);
  }, [exclusionsQuery.error, handleErrorMessage]);

  const marketTypesQuery = useInfiniteScroll<MarketType>({
    queryKey: [QUERY_KEYS.marketTypes, 'welcome-bonus-exclusion-picker'],
    endpoint: API_ROUTES.MARKET_TYPES,
    queryParams: {
      limit: 25,
      search: debouncedSearch || undefined,
      sportId: sportId || undefined,
      isActive: true,
      usedFor: usedFor ? ensureArray(usedFor) : undefined,
      type: type || undefined,
    },
  });

  // Hydrate once so background refetches cannot overwrite unsaved selections.
  useEffect(() => {
    if (!exclusionsQuery.data || hasInitializedDraft.current) return;

    dispatch({ type: 'hydrate', marketTypes: getMarketTypesFromExclusions(exclusionsQuery.data.items) });
    reset(MARKET_TYPE_EXCLUSIONS_DEFAULT_VALUES);
    hasInitializedDraft.current = true;
  }, [exclusionsQuery.data, reset]);

  // Filter changes clear pending checkboxes, but preserve accumulated exclusions.
  useEffect(() => {
    dispatch({ type: 'clearChecked' });

    if (listRef.current) listRef.current.scrollTop = 0;
  }, [debouncedSearch, sportId, type, usedFor]);

  const selectedMarketTypeIds = useMemo(
    () => new Set(state.selectedMarketTypes.map(({ id }) => id)),
    [state.selectedMarketTypes]
  );

  const checkedMarketTypeIds = useMemo(() => new Set(state.checkedMarketTypeIds), [state.checkedMarketTypeIds]);

  const marketTypesById = useMemo(
    () => new Map((marketTypesQuery.items || []).map((marketType) => [marketType.id, marketType])),
    [marketTypesQuery.items]
  );

  const eligibleMarketTypeIds = Array.from(marketTypesById.keys()).filter((id) => !selectedMarketTypeIds.has(id));
  const checkedEligibleCount = eligibleMarketTypeIds.filter((id) => checkedMarketTypeIds.has(id)).length;
  const hasEligibleMarketTypes = eligibleMarketTypeIds.length > 0;
  const allLoadedChecked = hasEligibleMarketTypes && checkedEligibleCount === eligibleMarketTypeIds.length;
  const someLoadedChecked = checkedEligibleCount > 0 && !allLoadedChecked;

  // Only the footer Save persists the individual-market draft.
  const updateMutation = useMutation({
    mutationFn: (payload: { marketTypeIds: string[] }) =>
      postData<BonusMarketTypeExclusionsResponse>(API_ROUTES.BONUS_MARKET_TYPE_EXCLUSIONS, payload),
    onSuccess: async () => {
      enqueueSnackbar({ message: 'Updated successfully.', variant: 'success' });

      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.bonusMarketTypeExclusions] });

      closeModal?.();
    },
    onError: handleErrorMessage,
  });

  // Stable row callbacks let memoized lists skip unrelated filter updates.
  const toggleMarketType = useCallback((marketTypeId: string) => dispatch({ type: 'toggle', marketTypeId }), []);
  const removeMarketType = useCallback((marketTypeId: string) => dispatch({ type: 'remove', marketTypeId }), []);

  const addSelected = () => {
    const marketTypesToAdd = getMarketTypesToAdd(marketTypesById, state.checkedMarketTypeIds, selectedMarketTypeIds);

    if (marketTypesToAdd.length) dispatch({ type: 'add', marketTypes: marketTypesToAdd });
  };

  const save = () => {
    const payload = { marketTypeIds: state.selectedMarketTypes.map(({ id }) => id) };

    updateMutation.mutate(payload);
  };

  const disableSave = exclusionsQuery.isLoading || exclusionsQuery.isError || updateMutation.isPending;

  const value: PickerContextValue = {
    state: {
      availableMarketTypes: marketTypesQuery.items || [],
      selectedMarketTypes: state.selectedMarketTypes,
      checkedMarketTypeIds,
      selectedMarketTypeIds,
      search: state.search,
      allLoadedChecked,
      someLoadedChecked,
      hasEligibleMarketTypes,
    },
    actions: {
      setSearch: (search) => dispatch({ type: 'search', search }),
      toggleMarketType,
      setAllLoadedChecked: (checked) => dispatch({ type: 'setChecked', marketTypeIds: eligibleMarketTypeIds, checked }),
      addSelected,
      removeMarketType,
      clearAll: () => dispatch({ type: 'clearAll' }),
      save,
    },
    meta: {
      control,
      sportOptions,
      listRef,
      lastElementRef: marketTypesQuery.lastElementRef,
      isLoading: marketTypesQuery.isLoading,
      isFetchingNextPage: marketTypesQuery.isFetchingNextPage,
      disableSave,
      closeModal,
    },
  };

  return <PickerContext.Provider value={value}>{children}</PickerContext.Provider>;
};

export const useMarketPicker = () => {
  const context = useContext(PickerContext);

  if (!context) throw new Error('useMarketPicker must be used within its provider');

  return context;
};
